/**
 * Phosphor Regular rendering shared by the three legacy icon paths:
 * the Vuetify `mdi-*` icon set, the `vue-tabler-icons` shim and DsIcon.
 *
 * BS Labs rule: Phosphor Regular, `fill: currentColor` so the glyph takes the
 * text colour of wherever it sits. The SVG markup comes from
 * @phosphor-icons/core via the generated registry (only the glyphs in use).
 */
import { h, type VNode } from 'vue';
import { PHOSPHOR_RAW } from './phosphorSvgs.generated';

const innerCache = new Map<string, string>();

/**
 * The inner markup of a Phosphor icon (its `<path>`), or null when the name is
 * not in the registry.
 *
 * Every path is given `fill="currentColor" stroke="none"` as attributes. The
 * old header and sidebar styles force `svg { fill: none !important; stroke: … }`
 * on tabler icons and stroke every `path:not([stroke='none'])`; a Phosphor
 * glyph is a filled shape, so inheriting `fill: none` would make it vanish and
 * a stroke would double its outline. The attributes opt the path out of both.
 */
export function phosphorInner(name: string): string | null {
    const cached = innerCache.get(name);
    if (cached !== undefined) return cached;
    const raw = PHOSPHOR_RAW[name];
    if (!raw) return null;
    const inner = raw
        .replace(/^[\s\S]*?<svg[^>]*>/, '')
        .replace(/<\/svg>\s*$/, '')
        .replace(/<path\b/g, '<path fill="currentColor" stroke="none"');
    innerCache.set(name, inner);
    return inner;
}

export function hasPhosphor(name: string | null | undefined): name is string {
    return !!name && !!PHOSPHOR_RAW[name];
}

/** Turn `18`, `'18'` or `'1.25rem'` into a CSS length. */
export function toCssSize(size: number | string | undefined | null, fallback: number): string {
    if (size === undefined || size === null || size === '') return `${fallback}px`;
    return typeof size === 'number' || /^\d+(\.\d+)?$/.test(size) ? `${size}px` : size;
}

// Inline, not a stylesheet, so a standalone icon keeps its box even inside a
// scoped component whose CSS was written for tabler's 24px stroke icons: the
// fixed box and flex-shrink stop rows and buttons from reflowing.
const STANDALONE_STYLE = {
    display: 'inline-block',
    verticalAlign: 'middle',
    flexShrink: '0',
    fill: 'currentColor'
};

export interface PhosphorSvgOptions {
    size?: number | string;
    /** Accessible name. Without one the icon is decorative and hidden from assistive tech. */
    label?: string;
    class?: unknown;
    /** Extra attributes (listeners, data-*, style, color, …) merged onto the <svg>. */
    attrs?: Record<string, unknown>;
}

/**
 * A standalone Phosphor `<svg>` (tabler shim, DsIcon). Returns null for a name
 * that is not in the registry so the caller can fall back.
 */
export function phosphorSvg(name: string, opts: PhosphorSvgOptions = {}): VNode | null {
    const inner = phosphorInner(name);
    if (inner === null) return null;
    const { style: extraStyle, ...rest } = opts.attrs ?? {};
    const px = toCssSize(opts.size, 18);
    return h('svg', {
        xmlns: 'http://www.w3.org/2000/svg',
        viewBox: '0 0 256 256',
        width: px,
        height: px,
        fill: 'currentColor',
        focusable: 'false',
        ...(opts.label ? { role: 'img', 'aria-label': opts.label } : { 'aria-hidden': 'true' }),
        ...rest,
        class: ['bs-ph', `bs-ph-${name}`, opts.class],
        style: [STANDALONE_STYLE, extraStyle as any],
        innerHTML: inner
    });
}
